import { uploadImageToS3 } from '@/utils/imageUtils';
import { v4 as uuidv4 } from 'uuid';
import { Mutex } from './mutex';

(function () {
    'ngInject';

    angular.module('app.shared.services').factory('ProductService', productService);

    function productService(ProductAPIService, ImageUtilService, GeneralMathUtils, ElementsColorService, $q) {
        // Default structure
        let productStructure = {
            id: '',
            name: '',
            referenceId: '',
            format: '',
            colorMode: '',
            MergeFiles: false,
            preview: {
                imagePath: '',
                bgColor: '',
                width: 0,
                height: 0,
                textsPreview: [],
                circularTextsPreview: [],
                imagePlaceHoldersPreview: [],
                vectorEPSPreview: [],
                textTextures: []
            },
            listEPS: [],
            groups: []
        };
        var product = JSON.parse(JSON.stringify(productStructure));
        var needsColorUpgrade = false;
        var productTemplatePreviewTargetSize = null;
        var podAssignments = null;
        var selectedPod = null;
        var templateProductVariants = null;
        var childTemplateProduct = null;
        var previewColorOptionsMetadata = null;
        var productColorsFilename = [];
        var productDescription = '';
        var isProductColorModified = false;
        var starMapMutex = new Mutex();
        var qrMutex = new Mutex();

        console.log('StarMap mutex was created');

        function getStarMapMutex() {
            return starMapMutex;
        }

        function getQrMutex() {
            return qrMutex;
        }

        let isProductEditMode = true;
        /**
         * This method adds any missing groups in preview due to new groups added in eps (step 1). New groups added
         *  in step 1 have the property "addedOnEdit = true". This method adds the same group in the preview step and
         *  re-assigns the groupIds for the preview elements.
         */
        function updatePreviewGroups() {
            const previewElements = [
                ...product.preview.textsPreview,
                ...product.preview.circularTextsPreview,
                ...product.preview.imagePlaceHoldersPreview,
                ...product.preview.vectorEPSPreview
            ];

            const epsElements = [];
            const listEps = getListEPS();
            for (const eps of listEps) {
                epsElements.push(
                    ...eps.textsEps,
                    ...eps.circularTextsEps,
                    ...eps.imagePlaceHoldersEps,
                    ...eps.vectorEPSEps
                );
            }

            const newAddedGroups = getGroups()
                .filter((g) => g.type === 'eps' && g.addedOnEdit)
                .sort((a, b) => {
                    // groups without parentId should be first
                    if (!a.parentId && b.parentId) return -1;
                    if (a.parentId && !b.parentId) return 1;
                    return 0;
                });

            for (const group of newAddedGroups) {
                const previewGroup = {
                    ...group,
                    addedOnEdit: false,
                    type: 'preview',
                    id: uuidv4()
                };

                if (previewGroup.parentId) {
                    const epsParentGroup = getGroups().find((g) => g.type === 'eps' && g.id === previewGroup.parentId);
                    if (epsParentGroup) {
                        const previewParentGroup = getGroups().find(
                            (g) => g.type === 'preview' && g.uuid === epsParentGroup.uuid
                        );
                        if (previewParentGroup) {
                            previewGroup.parentId = previewParentGroup.id;
                        }
                    }
                }

                addGroups([previewGroup]);

                const epsElementsAttachedToTheGroup = epsElements.filter((e) => e.groupId === group.id);
                const previewElementsAttachedToTheGroup = [];

                for (const epsElement of epsElementsAttachedToTheGroup) {
                    const previewElementFound = previewElements.find((e) => e.uuid === epsElement.uuid);
                    if (previewElementFound) previewElementsAttachedToTheGroup.push(previewElementFound);
                }
                for (const previewElement of previewElementsAttachedToTheGroup) {
                    previewElement.groupId = previewGroup.id;
                }

                group.addedOnEdit = false;
            }

            //#region need prop activation
            const epsGroups = getGroups().filter((g) => g.type === 'eps');
            const previewGroups = getGroups().filter((g) => g.type === 'preview');
            // sync position eps groups with preview groups
            for (const epsGroup of epsGroups) {
                const previewGroup = previewGroups.find((g) => g.uuid === epsGroup.uuid);
                if (previewGroup) {
                    previewGroup.position = epsGroup.position;
                }
            }

            // update preview groups elements from eps elements
            for (const epsElement of epsElements) {
                const previewElementFound = previewElements.find((e) => e.uuid === epsElement.uuid);
                if (previewElementFound) {
                    const epsGroup = epsGroups.find((g) => g.id === epsElement.groupId);
                    if (epsGroup) {
                        const previewGroup = previewGroups.find((g) => g.uuid === epsGroup.uuid);
                        previewElementFound.groupId = previewGroup?.id || null;
                    }
                }
            }
            //#endregion
        }

        function getGroups() {
            return product.groups;
        }

        function addGroups(groups) {
            product.groups.push(...groups);
        }

        /**
         * Gets the eps by id.
         * If the id is null, returns the first eps in the list.
         * @param {Number}  epsId eps id (by default null)
         */
        function getEps(epsId = null) {
            if (epsId) {
                return product.listEPS.find((x) => x.id == epsId);
            }

            return (product.listEPS && product.listEPS[0]) || product.eps;
        }

        function getElementsIdAndTestingValues() {
            try {
                const listEps = getEps();
                const elements = [];
                const uuids = [];
                if (product.listEPS.length >= 2) {
                    for (const eps of product.listEPS) {
                        elements.push(...eps.textsEps);
                        elements.push(...eps.circularTextsEps);
                        elements.push(...eps.imagePlaceHoldersEps);
                        elements.push(...eps.vectorEPSEps);
                    }
                } else if (listEps) {
                    elements.push(...listEps.textsEps);
                    elements.push(...listEps.circularTextsEps);
                    elements.push(...listEps.imagePlaceHoldersEps);
                    elements.push(...listEps.vectorEPSEps);
                }
                elements?.forEach((element) => {
                    uuids.push({ uuid: element.uuid, value: element.testingText || element.testingUrl || element.svgString });
                });
                return uuids;
            } catch (error) {
                console.error(error);
            }
        }

        function applyTestingValues(elementArray, prop, valueMap, needImageRender, canvas) {
            if (!elementArray) return;
            for (const element of elementArray) {
                if (!valueMap[element.uuid]) continue;
                if (needImageRender) {
                    if (!canvas) continue;
                    const object = canvas.getObjects().find(
                        o => o.type === 'image-placeholder' && o.uuid === element.uuid
                    );
                    if (!object?.visible) continue;
                    const imgAux = document.createElement('img');
                    imgAux.onload = function () {
                        object.setElement(imgAux);
                        canvas.renderAll();
                    };
                    imgAux.src = valueMap[element.uuid];
                    object.testingUrl = imgAux.src;
                }
                element[prop] = valueMap[element.uuid];
            }
        }

        function setTestingValues(values) {
            const valueMap = Object.fromEntries(values.map((item) => [item.elementId, item.value]));

            for (const obj of product.listEPS) {
                applyTestingValues(obj.textsEps, 'testingText', valueMap);
                applyTestingValues(obj.circularTextsEps, 'testingText', valueMap);
                applyTestingValues(obj.vectorEPSEps, 'svgString', valueMap);
            }

            if (product.preview) {
                applyTestingValues(product.preview.textsPreview, 'testingText', valueMap);
                applyTestingValues(product.preview.circularTextsPreview, 'testingText', valueMap);
                applyTestingValues(product.preview.vectorEPSPreview, 'svgString', valueMap);
            }
        }

        function setImagesTestingValues(values, canvas) {
            const valueMap = Object.fromEntries(values.map((item) => [item.elementId, item.value]));
            for (const obj of product.listEPS) {
                applyTestingValues(obj.imagePlaceHoldersEps, 'testingUrl', valueMap, true, canvas);
            }
            if (product.preview) {
                applyTestingValues(product.preview.imagePlaceHoldersPreview, 'testingUrl', valueMap, true, canvas);
            }
        }

        function getListEPS() {
            return product.listEPS;
        }

        function AssingObj(source = {}, target = {}) {
            const propStrict = Object.keys(target);
            return Object.keys(source).reduce((result, prop) => {
                if (propStrict.includes(prop)) {
                    result[prop] = source[prop];
                }
                return result;
            }, target);
        }

        /**
         * Adds a new eps with default values.
         */
        function addNewEPS(eps = {}, singleEps = false) {
            const epsElement = AssingObj(eps, getDefaultEps());
            epsElement.zIndexEps = eps.zIndex;
            if (!singleEps) epsElement.zIndexPreview = null;
            product.listEPS.push(epsElement);
        }

        function addListEPS(listEps = []) {
            for (const eps of listEps) {
                const epsElement = AssingObj(eps, getDefaultEps());
                epsElement.zIndexEps = eps.zIndex;
                if (epsElement.zIndexPreview == null) epsElement.zIndexPreview = eps.zIndex;
                epsElement.name = eps.name;
                product.listEPS.push(epsElement);
            }
        }

        function updateEPSFromPreview(listEps = []) {
            for (const eps of listEps) {
                if (eps.type !== 'preview') continue;
                const elementEps = getEps(eps.id);
                elementEps.zIndexPreview = eps.zIndex;
            }
        }

        function deleteEPS(epsId) {
            product.listEPS = product.listEPS.filter((x) => x.id !== epsId);
        }

        /**
         * Returns an eps with default values.
         */
        function getDefaultEps() {
            return {
                id: 1,
                width: 0,
                height: 0,
                dpi: 300,
                exportPPI: 0,
                mirror: false,
                mergeOutput: false,
                baseImage: '',
                baseEps: '',
                textsEps: [],
                circularTextsEps: [],
                imagePlaceHoldersEps: [],
                vectorEPSEps: [],
                name: 'Page',
                zIndexEps: 0,
                zIndexPreview: 0,
                podPlaceholder: null,
                measureUnit: 'mm'
            };
        }

        function setPreviewColorOptionsMetadata(paths) {
            previewColorOptionsMetadata = paths;
        }

        function getPreviewColorOptionsMetadata() {
            return previewColorOptionsMetadata;
        }

        function setPodAssignments(assignments) {
            podAssignments = assignments;
        }

        function getPodAssignments() {
            return podAssignments;
        }

        function getPod() {
            return selectedPod;
        }

        function setPod(pod) {
            selectedPod = pod;
        }

        function setIsProductColorModified(productColorModified) {
            isProductColorModified = productColorModified;
        }
        function getIsProductColorModified() {
            return isProductColorModified;
        }
        function getProductColorsFilenames() {
            return productColorsFilename;
        }

        function setDescription(description) {
            productDescription = description;
        }

        function getDescription() {
            return productDescription;
        }
        function setProductColorsFilenames(filenamesArray) {
            const newFiles = filenamesArray.filter((f) => f.optionId > productColorsFilename.length);

            if (newFiles.length) {
                productColorsFilename = [...productColorsFilename, ...newFiles];
            }
            if (productColorsFilename.length) {
                productColorsFilename.map((file) => {
                    const updatedFilename = filenamesArray.find((f) => f.optionId == file.optionId);
                    if (updatedFilename) file.name = updatedFilename.name;
                });
            }
        }
        function setTemplateProductVariants(assignments) {
            templateProductVariants = assignments;
        }

        function getTemplateProductVariants() {
            return templateProductVariants;
        }

        function setChildTemplateProduct(templateProduct) {
            childTemplateProduct = templateProduct;
        }

        function getChildTemplateProduct() {
            return childTemplateProduct;
        }

        function clearChildTemplateProduct() {
            childTemplateProduct = {
                parentId: null,
                forChildTemplateProductCreation: null,
                oldPrintVariantName: null,
                newPrintVariantName: null,
                currentTemplate: {
                    oldVariant: null,
                    newVariant: null,
                    dimension: {
                        width: null,
                        height: null,
                        unit: null
                    }
                },
                savedTemplates: [],
                isCompleted: null
            };
        }

        function setProductTemplatePreviewTargetSize(width, height) {
            productTemplatePreviewTargetSize = {
                width: width,
                height: height
            };
        }

        function getProductTemplatePreviewTargetSize() {
            return productTemplatePreviewTargetSize;
        }

        function loadProduct(p) {
            clearProduct();
            product = p;
            product.listEPS = p.listEPS || [p.eps];

            const groups = product.groups || [];

            // Remove orphan groupsIds from canvas elements
            product.listEPS.forEach((eps) => {
                const elements = [
                    ...(eps.textsEps || []),
                    ...(eps.circularTextsEps || []),
                    ...(eps.imagePlaceHoldersEps || []),
                    ...(eps.vectorEPSEps || [])
                ].filter((element) => element?.groupId);

                elements.forEach((element) => {
                    if (element.groupId && !groups.some((group) => group.id === element.groupId)) {
                        element.groupId = null;
                    }
                });
            });
        }

        function clearProduct() {
            product = JSON.parse(JSON.stringify(productStructure));
            needsColorUpgrade = false;
            productTemplatePreviewTargetSize = null;
            podAssignments = null;
            previewColorOptionsMetadata = null;
            selectedPod = null;
        }

        function clearEPS() {
            if (typeof product.eps !== 'undefined') delete product.eps;
            product.listEPS = JSON.parse(JSON.stringify(productStructure)).listEPS;
            product.groups = product.groups.filter((g) => g.type !== 'eps');
        }

        function clearPreview() {
            product.preview = JSON.parse(JSON.stringify(productStructure)).preview;
            product.groups = product.groups.filter((g) => g.type !== 'preview');
            productTemplatePreviewTargetSize = null;
            previewColorOptionsMetadata = null;
        }

        function clearPreviewElements() {
            product.preview = {
                ...product.preview,
                textsPreview: [],
                circularTextsPreview: [],
                imagePlaceHoldersPreview: [],
                vectorEPSPreview: [],
                textTextures: []
            };
            product.groups = product.groups.filter((g) => g.type !== 'preview');
        }

        function setProductId(id) {
            product.id = id;
        }

        function getProductId() {
            return product.id;
        }

        function setProductName(name) {
            product.name = name;
        }

        function setMelcoReferenceId(referenceId) {
            product.referenceId = referenceId;
        }

        function setProductFormat(format) {
            product.format = format;
        }

        function setProductColorMode(model) {
            product.colorMode = model;
        }

        function setMergeFiles(mergeFiles = false) {
            product.MergeFiles = mergeFiles;
        }

        function setEPSMirroring(mirror, epsId = null) {
            let eps = getEps(epsId);
            eps.mirror = mirror;
        }

        function setEpsMergeOutput(merge, epsId = null) {
            let eps = getEps(epsId);
            eps.mergeOutput = merge;
        }

        function getPreviewImage() {
            return product.preview.imagePath;
        }

        function getPreviewBgColor() {
            return product.preview.bgColor;
        }

        function setNeedColorsUpgrade(state) {
            needsColorUpgrade = state;
        }

        function getNeedColorsUpgrade() {
            return needsColorUpgrade;
        }

        function setEditMode(state) {
            isProductEditMode = state;
        }

        function getEditMode() {
            return isProductEditMode;
        }

        /**
         * Set the product preview image as a relative path in the form of:
         *     '/Content/product-images/my-image.png'
         */
        function setPreviewImage(imagePath) {
            product.preview.imagePath = imagePath;
        }

        function setPreviewBgColor(color) {
            product.preview.bgColor = color;
        }

        function setPreviewImgSize(width, height) {
            product.preview.width = width;
            product.preview.height = height;
        }

        function setProductSnapshotUrl(imagePath) {
            product.snapshotUrl = imagePath;
        }

        function getProductSnapshotUrl() {
            return product.snapshotUrl;
        }

        function setProductThumbnail(imagePath) {
            product.preview.thumbnailPath = imagePath;
        }

        function getProductThumbnail() {
            return product.preview.thumbnailPath;
        }

        /**
         * Add a circular text for the preview image
         * @param {Number}  centerX     Center X position of the text in the preview image in pixels
         * @param {Number}  centerY     Center Y position of the text in the preview image in pixels
         * @param {String}  color       Color string in the form of #rrggbb
         * @param {Number}  rotation    Text rotation in degrees
         * @param {Number}  skewX       Horizontal skew in degrees
         * @param {Number}  skewY       Vertical skew in degrees
         * @param {Number}  horizontalDiameter      Horizontal diameter in pixels
         * @param {Number}  verticalDiameter        Vertical diameter in pixels
         * @param {Number}  fontSize    Font size in pixels
         * @param {Bool}    caps        If text should be capitalized
         * @param {String}  fontPath    Path to the font to use in the form of /Content/fonts/arial.ttf
         * @param {String}  textAlign   Text alignment, valid values are 'center', 'left', 'right' and 'justify'
         * @param {Bool}    convex      Wether the text is convex or not (concave)
         * @param {String}  outlineColor    Color string of the text outline in the form of #rrggbb
         * @param {Number}  outlineWidth    Outline width (integer). If no outline is applied this should be 0
         * @param {Number}  zIndex      z-index of this element, 0 being the deepest level
         * @param {Number}  id          Unique id of this text
         * @param {Map}     fontsMap Fonts Map
         * @param {Map}     fontColorsMap Font Color Map
         */
        function addPreviewCircularText(
            centerX,
            centerY,
            color,
            rotation,
            skewX,
            skewY,
            horizontalDiameter,
            verticalDiameter,
            fontSize,
            minSizePx,
            maxSizePx,
            caps,
            fontPath,
            textAlign,
            convex,
            tracking,
            outlineColor,
            outlineWidth,
            outlineColorsMap,
            outlineColorLibraryId,
            zIndex,
            id,
            uuid,
            fontsMap,
            fontColorsMap,
            colorLibraryId,
            fontLibraryId,
            startAngle,
            endAngle,
            addedOnEdit,
            locked,
            visible,
            name,
            prefix,
            suffix,
            referenceId,
            isPrintArea,
            isOverlay,
            groupId,
            pageEpsId = null,
            flipX = false,
            flipY = false,
            testingText = '',
            clipPath
        ) {
            let text = {
                id: id,
                uuid: uuid,
                centerX: centerX,
                centerY: centerY,
                color: typeof color === 'object' ? JSON.stringify(color) : color,
                rotation: rotation,
                horizontalDiameter: horizontalDiameter,
                verticalDiameter: verticalDiameter,
                fontSize: maxSizePx ?? fontSize,
                minSizePx,
                maxSizePx,
                caps: caps,
                fontPath: fontPath,
                textAlign: textAlign,
                convex: convex,
                tracking,
                outlineColor: outlineColor,
                outlineWidth: outlineWidth,
                outlineColorsMap,
                outlineColorLibraryId,
                skewX: skewX,
                skewY: skewY,
                zIndex: zIndex,
                // add fonts map
                fontsMap: fontsMap,
                // add font colors map
                fontColorsMap: fontColorsMap,
                colorLibraryId: colorLibraryId,
                fontLibraryId: fontLibraryId,
                startAngle: startAngle,
                endAngle: endAngle,
                addedOnEdit: addedOnEdit, // Internal property, not stored in the database
                locked: locked,
                visible: visible,
                name: name,
                prefix: prefix,
                suffix: suffix,
                referenceId: referenceId,
                isPrintArea: isPrintArea,
                isOverlay: isOverlay,
                groupId: groupId,
                pageEpsId,
                flipX,
                flipY,
                testingText,
                clipPath
            };
            product.preview.circularTextsPreview.push(text);
        }

        /**
         * Add a text for the preview image
         * @param {Number}  centerX     Center X position of the text in the preview image in pixels
         * @param {Number}  centerY     Center Y position of the text in the preview image in pixels
         * @param {String}  color       Color string in the form of #rrggbb
         * @param {Number}  rotation    Text rotation in degrees
         * @param {Number}  skewX       Horizontal skew in degrees
         * @param {Number}  skewY       Vertical skew in degrees
         * @param {Number}  width       Text bounds width in pixels
         * @param {Number}  height      Text bounds height in pixels
         * @param {Number}  minSizePx   Minimum font size in pixels
         * @param {Number}  maxSizePx   Maximum font size in pixels
         * @param {Bool}    multiline   If multiline is allowed
         * @param {Bool}    caps        If text should be capitalized
         * @param {String}  fontPath    Path to the font to use in the form of /Content/fonts/arial.ttf
         * @param {String}  textAlign   Text alignment, valid values are 'center', 'left', 'right' and 'justify'
         * @param {String}  outlineColor    Color string of the text outline in the form of #rrggbb
         * @param {Number}  outlineWidth    Outline width (integer). If no outline is applied this should be 0
         * @param {Number}  zIndex      z-index of this element, 0 being the deepest level
         * @param {Number}  addedOnEdit If the text was added on the previous step.
         * @param {Number}  id          Unique id of this text
         * @param {String}  textVerticalAlign   Text vertical alignment, valid values are 'center', 'top' and 'bottom'
         * @param {Bool}    allowEdit  If the text can be edited in the preview
         * @param {String}  pageEpsId  The id of the EPS page where the text is placed
         * @param {Bool}    flipX      If the text is flipped horizontally
         * @param {Bool}    flipY      If the text is flipped vertically
         * @param {String}  testingText  Text to be used for testing purposes
         * @param {Bool}    isTextPath  If the text is a path
         * @param {Object}  transformOptions  Transform options for the text
         * @param {Boolean}  useAsMask  Use text as mask
         * @param {Bool}    verticalText   If verticalText is enabled
         */
        function addPreviewText(
            centerX,
            centerY,
            color,
            rotation,
            skewX,
            skewY,
            width,
            height,
            minSizePx,
            maxSizePx,
            multiline,
            caps,
            fontPath,
            textAlign,
            outlineColor,
            outlineWidth,
            outlineColorsMap,
            outlineColorLibraryId,
            zIndex,
            id,
            uuid,
            fontsMap,
            fontColorsMap,
            Itext,
            textures,
            tracking,
            lineSpacing,
            colorLibraryId,
            fontLibraryId,
            version,
            addedOnEdit,
            locked,
            visible,
            name,
            prefix,
            suffix,
            ligatures,
            referenceId,
            isPrintArea,
            isOverlay,
            groupId,
            textVerticalAlign,
            allowEdit,
            oldAllowEdit,
            initCenterX,
            initCenterY,
            initRotation,
            initFontSize,
            pageEpsId = null,
            flipX = false,
            flipY = false,
            testingText = '',
            isTextPath = false,
            transformOptions = {},
            clipPath,
            useAsMask,
            verticalText,
            autofillSource,
            dateFormat,
            strokeColor = '',
            strokeWidth = 0,
            strokeColorsMap = null,
            strokeColorLibraryId = null,
            shadowType,
            shadowOffset,
            shadowAngle,
            shadowBlur,
            shadowColor,
            shadowColorsMap,
            shadowColorLibraryId
        ) {
            let text = {
                id: id,
                uuid: uuid,
                centerX: centerX,
                centerY: centerY,
                color: typeof color === 'object' ? JSON.stringify(color) : color,
                rotation: rotation,
                width: width,
                height: height,
                minSizePx: minSizePx,
                maxSizePx: maxSizePx,
                multiline: multiline,
                verticalText: verticalText,
                caps: caps,
                fontPath: fontPath,
                textAlign: textAlign,
                outlineColor: typeof outlineColor === 'object' ? JSON.stringify(outlineColor) : outlineColor,
                outlineWidth: outlineWidth,
                outlineColorsMap,
                outlineColorLibraryId,
                skewX: skewX,
                skewY: skewY,
                zIndex: zIndex,
                fontsMap: fontsMap,
                fontColorsMap: fontColorsMap,
                Itext: Itext,
                textures: textures,
                tracking: tracking,
                lineSpacing: lineSpacing,
                colorLibraryId: colorLibraryId,
                fontLibraryId: fontLibraryId,
                Version: version,
                addedOnEdit: addedOnEdit, // Internal property, not stored in the database
                locked: locked,
                visible: visible,
                name: name,
                prefix: prefix,
                suffix: suffix,
                ligatures: ligatures,
                referenceId: referenceId,
                isPrintArea: isPrintArea,
                isOverlay: isOverlay,
                groupId: groupId,
                textVerticalAlign,
                allowEdit,
                oldAllowEdit,
                initCenterX,
                initCenterY,
                initRotation,
                initFontSize,
                pageEpsId,
                flipX,
                flipY,
                testingText: testingText,
                isTextPath,
                transformOptions,
                clipPath,
                useAsMask,
                autofillSource,
                dateFormat,
                strokeColor: typeof strokeColor === 'object' ? JSON.stringify(strokeColor) : strokeColor,
                strokeWidth,
                strokeColorsMap,
                strokeColorLibraryId,
                shadowType,
                shadowOffset,
                shadowAngle,
                shadowBlur,
                shadowColor: typeof shadowColor === 'object' ? JSON.stringify(shadowColor) : shadowColor,
                shadowColorsMap,
                shadowColorLibraryId
            };
            product.preview.textsPreview.push(text);
        }

        /**
         * Delete the linked preview element for the given element in the production file.
         * @param {Object} object Canvas object on the production file to delete on the preview.
         */
        function removeFromPreview(object, objDelete = true) {
            // Use UUID if available otherwise fallback to ID
            let uuIdOrId = object.uuid || object.id;

            if (typeof uuIdOrId === 'undefined' || uuIdOrId == null) return;
            let indexText = product.preview.textsPreview.findIndex(function (x) {
                return x.uuid === uuIdOrId || x.id === uuIdOrId;
            });
            if (indexText >= 0) {
                product.preview.textsPreview[indexText].deleteFromEps = objDelete;
            }

            let indexCircularText = product.preview.circularTextsPreview.findIndex(function (x) {
                return x.uuid === uuIdOrId || x.id === uuIdOrId;
            });
            if (indexCircularText >= 0) {
                product.preview.circularTextsPreview[indexCircularText].deleteFromEps = objDelete;
            }

            let indexPlaceholder = product.preview.imagePlaceHoldersPreview.findIndex(function (x) {
                return x.uuid === uuIdOrId || x.id === uuIdOrId;
            });
            if (indexPlaceholder >= 0) {
                product.preview.imagePlaceHoldersPreview[indexPlaceholder].deleteFromEps = objDelete;
            }

            let indexVectorEPS = product.preview.vectorEPSPreview.findIndex(function (x) {
                return x.uuid === uuIdOrId || x.id === uuIdOrId;
            });
            if (indexVectorEPS >= 0) {
                product.preview.vectorEPSPreview[indexVectorEPS].deleteFromEps = objDelete;
            }

            let indexGroup = getGroups().findIndex(function (x) {
                return x.uuid === uuIdOrId || x.id === uuIdOrId;
            });
            if (indexGroup >= 0) {
                let group = getGroups()[indexGroup];
                group.deleteFromEps = objDelete;
                if (!objDelete) group.addedOnEdit = false;
            }
        }

        function recoverFromPreview(object) {
            removeFromPreview(object, false);
        }

        function clearObjectsDeleteOnEPS() {
            product.preview.textsPreview = product.preview.textsPreview.filter(function (x) {
                return !x.deleteFromEps;
            });
            product.preview.circularTextsPreview = product.preview.circularTextsPreview.filter(function (x) {
                return !x.deleteFromEps;
            });
            product.preview.imagePlaceHoldersPreview = product.preview.imagePlaceHoldersPreview.filter(function (x) {
                return !x.deleteFromEps;
            });
            product.preview.vectorEPSPreview = product.preview.vectorEPSPreview.filter(function (x) {
                return !x.deleteFromEps;
            });
            product.groups = product.groups.filter(function (x) {
                return !x.deleteFromEps;
            });
        }

        /**
         * Add a Image for the Preview
         * @param {Object}  data
         */
        function addPreviewPlaceholderImage(data) {
            const {
                centerX,
                centerY,
                rotation,
                skewX,
                skewY,
                width,
                height,
                color,
                fullColor,
                grading,
                opacity,
                zIndex,
                id,
                uuid,
                hasStaticImage,
                imageUrl,
                maskPath,
                dynamicImagesPath,
                imageLibraryId,
                colors,
                colorLibraryId,
                rotationStep,
                coverMaskArea,
                removeWhiteBackground,
                RemoveBackground,
                addedOnEdit,
                locked,
                visible,
                name,
                isPrintArea,
                isOverlay,
                isColorOptions,
                podProductImagesMetadata,
                groupId,
                mapConfig,
                starMapConfig,
                cartoonize,
                CutoutFace,
                removeBackgroundCOP,
                CustomilyRemoveBackground = false,
                CustomilyFaceCutOut = false,
                CustomilyImageFilter = 0,
                pageEpsId = null,
                flipX = false,
                flipY = false,
                isLimitImageToPlaceholder,
                textToImage,
                imageToImage = false,
                imageToImageConfig = null,
                imageEffects = null,
                isGuide,
                allowEdit,
                crossword,
                CrosswordConfig = null,
                calendar = false,
                calendarConfig = null,
                enablePerspective = false,
                perspectiveCoords = null,
                outlineWidth,
                outlineColor,
                testingUrl,
                isProductGuidelines,
                clipPath,
                textMaskUuid,
                isMoonPhase = false,
                initCenterX,
                initCenterY,
                initWidth,
                initHeight,
                initRotation
            } = data;

            let image = {
                id: id,
                uuid: uuid,
                centerX: centerX,
                centerY: centerY,
                rotation: rotation,
                width: width,
                height: height,
                color: typeof color === 'object' ? JSON.stringify(color) : color,
                fullColor: fullColor,
                grading: grading,
                opacity: opacity,
                skewX: skewX,
                skewY: skewY,
                zIndex: zIndex,
                hasStaticImage: hasStaticImage,
                imageUrl: imageUrl,
                maskPath: maskPath,
                dynamicImagesPath:
                    dynamicImagesPath instanceof Map
                        ? JSON.stringify(Array.from(dynamicImagesPath.entries()))
                        : dynamicImagesPath,
                imageLibraryId: imageLibraryId,
                colors: colors,
                colorLibraryId: colorLibraryId,
                rotationStep: rotationStep,
                coverMaskArea: coverMaskArea,
                removeWhiteBackground: removeWhiteBackground,
                RemoveBackground: RemoveBackground,
                addedOnEdit: addedOnEdit, // Internal property, not stored in the database
                locked: locked,
                visible: visible,
                name: name,
                isPrintArea: isPrintArea,
                isOverlay: isOverlay,
                isColorOptions: isColorOptions,
                podProductImagesMetadata: podProductImagesMetadata,
                groupId: groupId,
                mapConfig: mapConfig,
                starMapConfig,
                cartoonize,
                CutoutFace,
                removeBackgroundCOP,
                CustomilyRemoveBackground,
                CustomilyFaceCutOut,
                CustomilyImageFilter,
                pageEpsId,
                flipX,
                flipY,
                isLimitImageToPlaceholder,
                textToImage,
                imageToImage,
                imageToImageConfig,
                imageEffects,
                isGuide,
                allowEdit,
                crossword,
                CrosswordConfig,
                calendar,
                calendarConfig,
                enablePerspective,
                perspectiveCoords:
                    perspectiveCoords instanceof Array ? JSON.stringify(perspectiveCoords) : perspectiveCoords,
                outlineWidth,
                outlineColor,
                testingUrl,
                isProductGuidelines,
                clipPath,
                textMaskUuid,
                isMoonPhase,
                initCenterX,
                initCenterY,
                initWidth,
                initHeight,
                initRotation
            };
            product.preview.imagePlaceHoldersPreview.push(image);
            return image;
        }

        /**
         * @param {Number}  centerX             Center X position of the vector EPS [pixels]
         * @param {Number}  centerY             Center Y position of the vector EPS [pixels]
         * @param {Number}  rotation            Rotation [degrees]
         * @param {Number}  skewX               Horizontal skew [degrees]
         * @param {Number}  skewY               Vertical skew [degrees]
         * @param {Number}  width               Width [pixels]
         * @param {Number}  height              Height [pixels]
         * @param {Number}  opacity             Opacity from 0 to 1
         * @param {Array}   vectors             Array of vectors for each position
         * @param colors
         * @param {Boolean} allowUserUpload     Whether to allow the user to upload an SVG element on the product preview
         * @param {Number}  zIndex              z-index of this element, 0 being the deepest level
         * @param {Number}  id                  Unique id of this vector EPS
         * @param id
         * @param uuid
         * @param colorLibraryId
         * @param vectorLibraryId
         * @param applyColoring
         * @param coverMaskArea
         * @param rotationStep
         * @param addedOnEdit
         * @param locked
         * @param visible
         * @param name
         * @param isPrintArea
         * @param isOverlay
         * @param groupId
         * @param starMapConfig
         * @param qrConfig
         * @param contentIsPdf
         * @param vectorContent
         * @param allowEdit
         * @param pageEpsId
         * @param flipX
         * @param flipY
         * @param isLimitImageToPlaceholder
         * @param clipPath
         * @param isSolidBackground
         */
        function addPreviewVectorEPS(
            centerX,
            centerY,
            rotation,
            skewX,
            skewY,
            width,
            height,
            opacity,
            vectors,
            colors,
            allowUserUpload,
            zIndex,
            id,
            uuid,
            colorLibraryId,
            vectorLibraryId,
            applyColoring,
            coverMaskArea,
            rotationStep,
            addedOnEdit,
            locked,
            visible,
            name,
            isPrintArea,
            isOverlay,
            groupId,
            starMapConfig,
            qrConfig,
            contentIsPdf,
            vectorContent,
            allowEdit,
            pageEpsId = null,
            flipX = false,
            flipY = false,
            isLimitImageToPlaceholder,
            clipPath,
            isSolidBackground
        ) {
            let eps = {
                id: id,
                uuid: uuid,
                centerX: centerX,
                centerY: centerY,
                rotation: rotation,
                width: width,
                height: height,
                opacity: opacity,
                skewX: skewX,
                skewY: skewY,
                vectors: vectors,
                allowUserUpload: allowUserUpload,
                zIndex: zIndex,
                colors: colors,
                colorLibraryId: colorLibraryId,
                vectorLibraryId: vectorLibraryId,
                applyColoring: applyColoring,
                coverMaskArea: coverMaskArea,
                rotationStep: rotationStep,
                addedOnEdit: addedOnEdit, // Internal property, not stored in the database
                locked: isSolidBackground ? false : locked,
                visible: visible,
                name: name,
                isPrintArea: isPrintArea,
                isOverlay: isOverlay,
                groupId: groupId,
                starMapConfig: starMapConfig,
                qrConfig: qrConfig,
                contentIsPdf: contentIsPdf,
                allowEdit: allowEdit,
                pageEpsId,
                flipX,
                flipY,
                isLimitImageToPlaceholder,
                clipPath,
                isSolidBackground
            };
            if (contentIsPdf) {
                eps.pdf = vectorContent;
            } else {
                eps.svgString = vectorContent;
            }
            product.preview.vectorEPSPreview.push(eps);
        }

        function setEPSSize(width, height, epsId = null) {
            let eps = getEps(epsId);
            eps.width = width;
            eps.height = height;
        }

        function setEPSdpi(dpi, epsId = null) {
            let eps = getEps(epsId);
            eps.dpi = dpi;
        }

        function setExportPPI(ppi, epsId = null) {
            let eps = getEps(epsId);
            eps.exportPPI = ppi;
        }

        /**
         * Add a text for the EPS
         * @param {Number}  centerX     Center X position of the text in the EPS in pixels
         * @param {Number}  centerY     Center Y position of the text in the EPS in pixels
         * @param {String}  color       Color string in the form of #rrggbb
         * @param {Number}  rotation    Text rotation in degrees
         * @param {Number}  skewX       Horizontal skew in degrees
         * @param {Number}  skewY       Vertical skew in degrees
         * @param {Number}  width       Text bounds width in pixels
         * @param {Number}  height      Text bounds height in pixels
         * @param {Number}  minSizePx   Minimum font size in pixels
         * @param {Number}  maxSizePx   Maximum font size in pixels
         * @param {Bool}    multiline   If multiline is allowed
         * @param {Bool}    caps        If text should be capitalized
         * @param {String}  fontPath    Path to the font to use in the form of /Content/fonts/arial.ttf
         * @param {String}  textAlign   Text alignment, valid values are 'center', 'left', 'right' and 'justify'
         * @param {String}  outlineColor    Color string of the text outline in the form of #rrggbb
         * @param {Number}  outlineWidth    Outline width (integer). If no outline is applied this should be 0
         * @param {Number}  zIndex      z-index of this element, 0 being the deepest level
         * @param {Number}  id          Unique id of this text
         * @param {String}  uuid        Unique identifier of this text
         * @param {Map}     fontsMap    Fonts Map
         * @param {Map}     fontColorsMap    Font Color Map
         * @param {String}  Itext       Fabric object
         * @param {Object}  editedProps Edited properties
         * @param {Object}  editedSize  Edited size
         * @param {Number}  tracking    Tracking amount
         * @param {Number}  lineSpacing Line spacing
         * @param {Number}  colorLibraryId Color Library Id
         * @param {Number}  fontLibraryId Font Library Id
         * @param {Number}  version     Version
         * @param {Boolean} locked      Locked
         * @param {Boolean} visible     Visible
         * @param {String}  name        Name
         * @param {String}  defaultValue Default Value
         * @param {String}  prefix      Prefix
         * @param {String}  suffix      Suffix
         * @param {Boolean} ligatures   Ligatures
         * @param {String}  referenceId Reference Id
         * @param {Boolean} isPrintArea Is Print Area
         * @param {Boolean} isOverlay   Is Overlay
         * @param {Number}  groupId     Group Id
         * @param {String}  textVerticalAlign   Text vertical alignment, valid values are 'center', 'top' and 'bottom'
         * @param {Boolean} allowEdit   Allow Edit
         * @param {Number}  pageEpsId   Page EPS Id
         * @param {Boolean} flipX       Flip X
         * @param {Boolean} flipY       Flip Y
         * @param {Array}   textures    Textures
         * @param {String}  testingText Testing Text
         * @param {Boolean} isTextPath  Is Text Path
         * @param {Object}  transformOptions Transform Options
         * @param {Boolean}  useAsMask Use text as mask
         * @param {Bool}    verticalText   If verticalText is enabled
         * @returns {Number} index of the newly added element
         */
        function addEPSText(
            centerX,
            centerY,
            color,
            rotation,
            skewX,
            skewY,
            width,
            height,
            minSizePx,
            maxSizePx,
            multiline,
            caps,
            fontPath,
            textAlign,
            outlineColor,
            outlineWidth,
            outlineColorsMap,
            outlineColorLibraryId,
            zIndex,
            id,
            uuid,
            fontsMap,
            fontColorsMap,
            Itext,
            editedProps,
            editedSize,
            tracking,
            lineSpacing,
            colorLibraryId,
            fontLibraryId,
            version,
            locked,
            visible,
            name,
            defaultValue,
            prefix,
            suffix,
            ligatures,
            referenceId,
            isPrintArea,
            isOverlay,
            groupId,
            textVerticalAlign,
            allowEdit,
            oldAllowEdit,
            initCenterX,
            initCenterY,
            initRotation,
            initFontSize,
            pageEpsId = null,
            flipX = false,
            flipY = false,
            textures,
            testingText = '',
            isTextPath = false,
            transformOptions = null,
            useAsMask = false,
            verticalText = false,
            autofillSource,
            dateFormat,
            strokeColor = '',
            strokeWidth = 0,
            strokeColorsMap = new Map(),
            strokeColorLibraryId = null,
            shadowType,
            shadowOffset,
            shadowAngle,
            shadowBlur,
            shadowColor,
            shadowColorsMap,
            shadowColorLibraryId
        ) {
            let eps = getEps(pageEpsId);
            if (fontsMap) {
                //if it has something stored on the map -> transform it to JSON because the database won't allow us to save a map
                fontsMap = JSON.stringify(Array.from(fontsMap.entries()));
            }
            if (fontColorsMap) {
                //if it has something stored on the map -> transform it to JSON because the database won't allow us to save a map
                fontColorsMap = JSON.stringify(Array.from(fontColorsMap.entries()));
            }

            if (outlineColorsMap) {
                outlineColorsMap = JSON.stringify(Array.from(outlineColorsMap.entries()));
            }

            if (strokeColorsMap) {
                strokeColorsMap = JSON.stringify(Array.from(strokeColorsMap.entries()));
            }

            if (shadowColorsMap) {
                shadowColorsMap = JSON.stringify(Array.from(shadowColorsMap.entries()));
            }

            let text = {
                id: id,
                uuid: uuid,
                centerX: centerX,
                centerY: centerY,
                color: typeof color === 'object' ? JSON.stringify(color) : color,
                rotation: rotation,
                width: width,
                height: height,
                minSizePx: minSizePx,
                maxSizePx: maxSizePx,
                multiline: multiline,
                verticalText: verticalText,
                caps: caps,
                fontPath: fontPath,
                textAlign: textAlign,
                outlineColor: typeof outlineColor === 'object' ? JSON.stringify(outlineColor) : outlineColor,
                outlineWidth: outlineWidth,
                outlineColorsMap,
                outlineColorLibraryId,
                skewX: skewX,
                skewY: skewY,
                zIndex: zIndex,
                fontsMap: fontsMap,
                fontColorsMap: fontColorsMap,
                Itext: Itext,
                editedProps: editedProps,
                editedSize: editedSize,
                tracking: tracking,
                lineSpacing: lineSpacing,
                colorLibraryId: colorLibraryId,
                fontLibraryId: fontLibraryId,
                Version: version,
                locked: locked,
                visible: visible,
                name: name,
                defaultValue: defaultValue,
                prefix: prefix,
                suffix: suffix,
                ligatures: ligatures,
                referenceId: referenceId,
                isPrintArea: isPrintArea,
                isOverlay: isOverlay,
                groupId: groupId,
                textVerticalAlign,
                allowEdit,
                oldAllowEdit,
                initCenterX,
                initCenterY,
                initRotation,
                initFontSize,
                pageEpsId,
                flipX,
                flipY,
                textures,
                testingText,
                isTextPath,
                transformOptions,
                useAsMask,
                autofillSource,
                dateFormat,
                strokeColor: typeof strokeColor === 'object' ? JSON.stringify(strokeColor) : strokeColor,
                strokeWidth,
                strokeColorsMap: strokeColorsMap,
                strokeColorLibraryId: strokeColorLibraryId,
                shadowType,
                shadowOffset,
                shadowAngle,
                shadowBlur,
                shadowColor: typeof shadowColor === 'object' ? JSON.stringify(shadowColor) : shadowColor,
                shadowColorsMap,
                shadowColorLibraryId
            };
            let size = eps.textsEps.push(text);
            return size - 1;
        }

        /**
         * Add a text to the product from the given object
         * @param {BoundedText} canvasObject Object to add to the product
         */
        function addEPSTextFromObject(canvasObject, skipPreviewElements = false) {
            return $q(function (resolve, reject) {
                // This is to prevent having the last selected font saved as the default one
                //  in the cases where there are several fonts stored in the same text placeholder we force the one in position (1) to be the default one,
                //  in other words, we store the first font in the fontsMap into fontPath
                if (canvasObject.fontsMap) {
                    canvasObject.fontPath = canvasObject.fontsMap.get(1);
                }
                if (canvasObject.fontColorsMap) {
                    canvasObject.color = canvasObject.fontColorsMap.get(1);
                }
                if (canvasObject.outlineColorsMap) {
                    canvasObject.outlineColor = canvasObject.outlineColorsMap.get(1);
                }
                if (canvasObject.strokeColorsMap) {
                    canvasObject.strokeColor = canvasObject.strokeColorsMap.get(1);
                }
                if (canvasObject.shadowColorsMap) {
                    canvasObject.shadowColor = canvasObject.shadowColorsMap.get(1);
                }

                const isTextPath = canvasObject.type === 'text-transform';

                let addedEpsElementIndex = addEPSText(
                    canvasObject.left,
                    canvasObject.top,
                    canvasObject.color,
                    canvasObject.angle,
                    canvasObject.skewX,
                    canvasObject.skewY,
                    canvasObject.getWidth(),
                    canvasObject.getHeight(),
                    canvasObject.minSizePx,
                    canvasObject.maxSizePx,
                    canvasObject.multiline,
                    canvasObject.caps,
                    canvasObject.fontPath,
                    canvasObject.getTextAlign(),
                    canvasObject.outlineColor,
                    canvasObject.getOutlineWidth(),
                    canvasObject.outlineColorsMap,
                    canvasObject.outlineColorLibraryId,
                    canvasObject.zIndex,
                    canvasObject.id,
                    canvasObject.uuid,
                    canvasObject.fontsMap,
                    canvasObject.fontColorsMap,
                    canvasObject.Itext,
                    canvasObject.editedProps,
                    canvasObject.editedSize,
                    canvasObject.getTrackingAmount(),
                    canvasObject.lineSpacing,
                    canvasObject.colorLibraryId,
                    canvasObject.fontLibraryId,
                    canvasObject.version,
                    canvasObject.locked,
                    canvasObject.visible,
                    canvasObject.name,
                    canvasObject.defaultValue,
                    canvasObject.prefix,
                    canvasObject.suffix,
                    canvasObject.ligatures,
                    canvasObject.referenceId,
                    canvasObject.isPrintArea,
                    canvasObject.isOverlay,
                    canvasObject.groupId,
                    canvasObject.textVerticalAlign,
                    canvasObject.allowEdit,
                    canvasObject.oldAllowEdit,
                    /*If the text uses the previous version of allowEdit (without a bounding box), these properties are left as null.
                    Once they have a value, the new version is considered active.*/
                    !canvasObject.oldAllowEdit && canvasObject.getTextLeft ? canvasObject.getTextLeft() : null,
                    !canvasObject.oldAllowEdit && canvasObject.getTextTop ? canvasObject.getTextTop() : null,
                    !canvasObject.oldAllowEdit && canvasObject.getTextAngle ? canvasObject.getTextAngle() : null,
                    !canvasObject.oldAllowEdit && canvasObject.getFontSize ? canvasObject.getFontSize() : null,
                    /* --- */
                    canvasObject.pageEpsId,
                    canvasObject.getFlip().flipX,
                    canvasObject.getFlip().flipY,
                    canvasObject.textures,
                    canvasObject.text,
                    isTextPath,
                    canvasObject.transformOptions || null,
                    canvasObject.useAsMask || false,
                    canvasObject.verticalText || false,
                    canvasObject.autofillSource || null,
                    canvasObject.dateFormat || {},
                    /* stroke */
                    canvasObject.strokeColor || null,
                    (canvasObject.getStrokeWidth && canvasObject.getStrokeWidth()) || 0,
                    canvasObject.strokeColorsMap || null,
                    canvasObject.strokeColorLibraryId || null,
                    /* shadow */
                    canvasObject.shadowType,
                    canvasObject.shadowOffset || 0,
                    canvasObject.shadowAngle || 0,
                    canvasObject.shadowBlur || 0,
                    canvasObject.shadowColor || null,
                    canvasObject.shadowColorsMap || null,
                    canvasObject.shadowColorLibraryId || null
                );
                updateProductionElementId(canvasObject, canvasObject.id);

                if (skipPreviewElements) {
                    resolve();
                    return;
                }

                // Added text on edit mode
                if (canvasObject.addedOnEdit) {
                    const objectCenterX = canvasObject.left;
                    const objectCenterY = canvasObject.top;
                    const boundingRectWidth = canvasObject.width;
                    const boundingRectHeight = canvasObject.height;
                    addPreviewText(
                        objectCenterX,
                        objectCenterY,
                        canvasObject.color,
                        canvasObject.angle,
                        canvasObject.skewX,
                        canvasObject.skewY,
                        boundingRectWidth,
                        boundingRectHeight,
                        canvasObject.minSizePx,
                        canvasObject.maxSizePx,
                        canvasObject.multiline,
                        canvasObject.caps,
                        canvasObject.fontPath,
                        canvasObject.getTextAlign(),
                        canvasObject.outlineColor,
                        canvasObject.getOutlineWidth(),
                        canvasObject.outlineColorsMap,
                        canvasObject.outlineColorLibraryId,
                        canvasObject.zIndex,
                        canvasObject.id,
                        canvasObject.uuid,
                        canvasObject.fontsMap,
                        canvasObject.fontColorsMap,
                        canvasObject.Itext,
                        canvasObject.textures,
                        canvasObject.getTrackingAmount(),
                        canvasObject.lineSpacing,
                        canvasObject.colorLibraryId,
                        canvasObject.fontLibraryId,
                        canvasObject.version,
                        true,
                        canvasObject.locked,
                        canvasObject.visible,
                        canvasObject.name,
                        canvasObject.prefix,
                        canvasObject.suffix,
                        canvasObject.ligatures,
                        canvasObject.referenceId,
                        canvasObject.isPrintArea,
                        canvasObject.isOverlay,
                        canvasObject.groupId,
                        canvasObject.textVerticalAlign,
                        canvasObject.allowEdit,
                        canvasObject.oldAllowEdit,
                        /*If the text uses the previous version of allowEdit (without a bounding box), these properties are left as null.
                        Once they have a value, the new version is considered active.*/
                        !canvasObject.oldAllowEdit && canvasObject.getTextLeft ? canvasObject.getTextLeft() : null,
                        !canvasObject.oldAllowEdit && canvasObject.getTextTop ? canvasObject.getTextTop() : null,
                        !canvasObject.oldAllowEdit && canvasObject.getTextAngle ? canvasObject.getTextAngle() : null,
                        !canvasObject.oldAllowEdit && canvasObject.getFontSize ? canvasObject.getFontSize() : null,
                        /* --- */
                        canvasObject.pageEpsId,
                        canvasObject.getFlip().flipX || false,
                        canvasObject.getFlip().flipY || false,
                        canvasObject.text,
                        isTextPath || false,
                        canvasObject.transformOptions || null,
                        '',
                        canvasObject.useAsMask || false,
                        canvasObject.verticalText || false,
                        canvasObject.autofillSource || '',
                        canvasObject.dateFormat || {},
                        /* stroke */
                        canvasObject.strokeColor || null,
                        (canvasObject.getStrokeWidth && canvasObject.getStrokeWidth()) || 0,
                        canvasObject.strokeColorsMap || null,
                        canvasObject.strokeColorLibraryId || null,
                        /* shadow */
                        canvasObject.shadowType,
                        canvasObject.shadowOffset || 0,
                        canvasObject.shadowAngle || 0,
                        canvasObject.shadowBlur || 0,
                        canvasObject.shadowColor || null,
                        canvasObject.shadowColorsMap || null,
                        canvasObject.shadowColorLibraryId || null
                    );
                    resolve();
                } else {
                    const updateEditedElement = function (epsElementToCheck, epsElement, previewElement, eps) {
                        // If we are editing the product, getProduct().preview.textsPreview won't be empty.
                        // If it is it means that we pressed the back button from step2 without saving the product, so in this case
                        //  we won't recalculate anything, as we'll be creating step2 elements as if it were the first item.

                        if (getProduct().preview.textsPreview && previewElement && epsElement && epsElementToCheck) {
                            // If the size changed, we have to recompute the size on the preview to keep the proportions the same
                            if (epsElementToCheck.editedSize) {
                                // Compute the element width and height
                                let scale = Math.min(
                                    getProduct().preview.width / eps.width,
                                    getProduct().preview.height / eps.height
                                );
                                let boundingRectWidth = epsElement.width * scale;
                                let boundingRectHeight = epsElement.height * scale;

                                // Update the element size in the preview
                                previewElement.width = boundingRectWidth;
                                previewElement.height = boundingRectHeight;

                                // Calculate the font sizes
                                let sizes = GeneralMathUtils.calculateFontSizesFromDimensions(
                                    boundingRectWidth,
                                    epsElement.width,
                                    epsElement.minSizePx,
                                    epsElement.maxSizePx
                                );
                                previewElement.minSizePx = sizes.min;
                                previewElement.maxSizePx = sizes.max;
                            }

                            // Copy edited props to the preview element
                            for (const prop of epsElementToCheck.editedProps) {
                                if (prop === 'textAlign') {
                                    previewElement.textAlign = epsElement.getTextAlign();
                                } else if (prop === 'tracking') {
                                    previewElement.tracking = epsElement.getTrackingAmount();
                                } else if (prop === 'outlineWidth') {
                                    const outlineWidth = GeneralMathUtils.calculateStrokeSizeFromDimensions(
                                        previewElement.width,
                                        epsElement.width,
                                        epsElement.getOutlineWidth()
                                    );
                                    previewElement.outlineWidth = outlineWidth;
                                } else if (prop == 'strokeWidth') {
                                    const strokeWidth = GeneralMathUtils.calculateStrokeSizeFromDimensions(
                                        previewElement.width,
                                        epsElement.width,
                                        epsElement.getStrokeWidth()
                                    );
                                    previewElement.strokeWidth = strokeWidth;
                                } else if (prop === 'transformOptions') {
                                    previewElement.transformOptions = epsElement.transformOptions || {};
                                    previewElement.needUpdatePreview = true;
                                } else if (prop === 'ungroup') {
                                    previewElement.groupId = null;
                                } else if (prop === 'innerText') {
                                    const values = GeneralMathUtils.calculateInitTextValues(
                                        previewElement.width,
                                        epsElement.width,
                                        epsElement.getTextLeft(),
                                        epsElement.getTextTop(),
                                        epsElement.getFontSize()
                                    );
                                    previewElement.initCenterX = values.initCenterX;
                                    previewElement.initCenterY = values.initCenterY;
                                    previewElement.initFontSize = values.initFontSize;
                                    previewElement.initRotation = epsElement.getTextAngle();
                                } else {
                                    previewElement[prop] = epsElement[prop];
                                }
                            }
                        }
                    };

                    // If the object was edited, update the parameters in the preview so it shows updated in step2
                    if (canvasObject.isEdited() || canvasObject.editedSize) {
                        let eps = getEps(canvasObject.pageEpsId);
                        // If the object doesn't have UUID use the old method to find the element in the preview
                        if (!canvasObject.uuid) {
                            let indexes = getAllIndexes(eps.textsEps, canvasObject.id);

                            if (indexes.length > 0) {
                                let duplicatedIds = true;

                                // Only one text with this ID? Find the index of the preview element with this ID.
                                // If we have more than one index that means we have duplicated texts IDs so just iterate
                                //  the previews element assuming they are in the same order as in the eps THIS IS NOT RIGHT
                                //  AND CAN FAIL, OLDER PRODUCTS WITH DUPLICATED IDs SHOULD BE RE-CREATED SO ELEMENTS ARE ASSIGNED
                                //  WITH A UUID INSTEAD
                                if (indexes.length == 1) {
                                    duplicatedIds = false;

                                    // Find the index of the preview element
                                    let index = getProduct().preview.textsPreview.findIndex(function (obj) {
                                        return obj.id === canvasObject.id;
                                    });

                                    if (index > -1) {
                                        indexes = [index];
                                    }
                                }

                                let occurrence = 0;
                                let lastElement = null;
                                for (const index of indexes) {
                                    // If we don't have duplicated ids don't assume elements order, find the EPS element
                                    //  by its id
                                    let epsIndex = index;
                                    if (!duplicatedIds) {
                                        epsIndex = addedEpsElementIndex;
                                    }
                                    const epsText = eps.textsEps[epsIndex];
                                    let previewText = getProduct().preview.textsPreview[index];

                                    // If the preview element id doesn't match the production or the preview element was not even found it's probably
                                    //  because the order of the elements for production and preview was not preserved, try to find it by id and occurrence
                                    //  as a best effort to find some element
                                    if (
                                        !previewText ||
                                        previewText.id !== epsText.id ||
                                        (lastElement && lastElement === previewText)
                                    ) {
                                        const previewTexts = getProduct().preview.textsPreview.filter(
                                            (e) => e.id === canvasObject.id
                                        );
                                        previewText = previewTexts[occurrence];

                                        // Don't select the same text more than once if we have the case where production and preview elements don't have
                                        //  the same order
                                        let n = occurrence;
                                        while (previewTexts.length > 1 && lastElement && lastElement === previewText) {
                                            previewText = previewTexts[++n];
                                        }

                                        lastElement = previewText;
                                    }

                                    if (previewText && previewText.id === epsText.id) {
                                        updateEditedElement(epsText, canvasObject, previewText, eps);
                                    } else {
                                        reject(new Error('Could not edit the text element with ID ' + canvasObject.id));
                                        return;
                                    }

                                    ++occurrence;
                                }
                            } else if (getProduct().preview.textsPreview.length > 0) {
                                reject(new Error('Could not edit the text element with ID ' + canvasObject.id));
                                return;
                            }
                        } else {
                            // If the element has a UUID find the preview elements by UUID
                            let textPreview = getProduct().preview.textsPreview.find(
                                (t) => t.uuid === canvasObject.uuid
                            );
                            if (!textPreview) {
                                console.error("Couldn't find preview element with UUID: " + canvasObject.uuid);
                            }
                            updateEditedElement(canvasObject, canvasObject, textPreview, eps);
                        }
                    }
                    resolve();
                }
            });
        }

        /**
         * Add a circular text for the EPS
         * @param {Number}  centerX     Center X position of the text in the preview image in pixels
         * @param {Number}  centerY     Center Y position of the text in the preview image in pixels
         * @param {String}  color       Color string in the form of #rrggbb
         * @param {Number}  rotation    Text rotation in degrees
         * @param {Number}  skewX       Horizontal skew in degrees
         * @param {Number}  skewY       Vertical skew in degrees
         * @param {Number}  horizontalDiameter      Horizontal diameter in pixels
         * @param {Number}  verticalDiameter        Vertical diameter in pixels
         * @param {Number}  fontSize    Font size in pixels
         * @param {Bool}    caps        If text should be capitalized
         * @param {String}  fontPath    Path to the font to use in the form of /Content/fonts/arial.ttf
         * @param {String}  textAlign   Text alignment, valid values are 'center', 'left', 'right' and 'justify'
         * @param {Bool}    convex      Wether the text is convex or not (concave)
         * @param {String}  outlineColor    Color string of the text outline in the form of #rrggbb
         * @param {Number}  outlineWidth    Outline width (integer). If no outline is applied this should be 0
         * @param {Number}  zIndex      z-index of this element, 0 being the deepest level
         * @param {Number}  id          Unique id of this text
         * @param {Map}     fontsMap Fonts Map
         * @param {Map}     fontColorsMap Font Colors Map
         */
        function addEPSCircularText(
            centerX,
            centerY,
            color,
            rotation,
            skewX,
            skewY,
            horizontalDiameter,
            verticalDiameter,
            fontSize,
            minSizePx,
            maxSizePx,
            caps,
            fontPath,
            textAlign,
            convex,
            tracking,
            outlineColor,
            outlineWidth,
            outlineColorsMap,
            outlineColorLibraryId,
            zIndex,
            id,
            uuid,
            fontsMap,
            fontColorsMap,
            colorLibraryId,
            fontLibraryId,
            startAngle,
            endAngle,
            locked,
            visible,
            name,
            defaultValue,
            prefix,
            suffix,
            referenceId,
            isPrintArea,
            isOverlay,
            groupId,
            pageEpsId = null,
            flipX = false,
            flipY = false,
            testingText = ''
        ) {
            let eps = getEps(pageEpsId);
            if (fontsMap) {
                //if it has something stored on the map -> transform it to JSON because the database won't allow us to save a map
                fontsMap = JSON.stringify(Array.from(fontsMap.entries()));
            }
            if (fontColorsMap) {
                //if it has something stored on the map -> transform it to JSON because the database won't allow us to save a map
                fontColorsMap = JSON.stringify(Array.from(fontColorsMap.entries()));
            }

            if (outlineColorsMap) {
                outlineColorsMap = JSON.stringify(Array.from(outlineColorsMap.entries()));
            }

            let text = {
                id: id,
                uuid: uuid,
                centerX: centerX,
                centerY: centerY,
                color: typeof color === 'object' ? JSON.stringify(color) : color,
                rotation: rotation,
                horizontalDiameter: horizontalDiameter,
                verticalDiameter: verticalDiameter,
                fontSize: maxSizePx ?? fontSize,
                minSizePx,
                maxSizePx,
                caps: caps,
                fontPath: fontPath,
                textAlign: textAlign,
                convex: convex,
                tracking,
                outlineColor: outlineColor,
                outlineWidth: outlineWidth,
                outlineColorsMap,
                outlineColorLibraryId,
                skewX: skewX,
                skewY: skewY,
                zIndex: zIndex,
                // add fonts map
                fontsMap: fontsMap,
                // add font colors map
                fontColorsMap: fontColorsMap,
                colorLibraryId: colorLibraryId,
                fontLibraryId: fontLibraryId,
                startAngle: startAngle,
                endAngle: endAngle,
                locked: locked,
                visible: visible,
                name: name,
                defaultValue: defaultValue,
                prefix: prefix,
                suffix: suffix,
                referenceId: referenceId,
                isPrintArea: isPrintArea,
                isOverlay: isOverlay,
                groupId: groupId,
                pageEpsId,
                flipX,
                flipY,
                testingText
            };
            eps.circularTextsEps.push(text);
        }

        /**
         * Add a circular text to the product from the given object
         * @param {PathIText} canvasObject Object to add to the product
         */
        function addEPSCircularTextFromObject(canvasObject, skipPreviewElements = false) {
            return $q(function (resolve, reject) {
                // This is to prevent having the last selected font saved as the default one
                // In the cases where there are several fonts stored in the same text placeholder we force the one in position (1) to be the default one
                // In other words, we store the first font in the fontsMap into fontPath
                if (canvasObject.fontsMap) {
                    canvasObject.fontPath = canvasObject.fontsMap.get(1);
                }
                if (canvasObject.fontColorsMap) {
                    canvasObject.color = canvasObject.fontColorsMap.get(1);
                }
                if (canvasObject.outlineColorsMap) {
                    canvasObject.outlineColor = canvasObject.outlineColorsMap.get(1);
                }

                addEPSCircularText(
                    canvasObject.left,
                    canvasObject.top,
                    canvasObject.color,
                    canvasObject.angle,
                    canvasObject.skewX,
                    canvasObject.skewY,
                    canvasObject.horizontalDiameter,
                    canvasObject.verticalDiameter,
                    canvasObject.fontSize,
                    canvasObject.minSizePx,
                    canvasObject.maxSizePx,
                    canvasObject.caps,
                    canvasObject.fontPath,
                    canvasObject.textAlign,
                    canvasObject.convex,
                    canvasObject.tracking,
                    canvasObject.stroke,
                    canvasObject.strokeWidth,
                    canvasObject.outlineColorsMap,
                    canvasObject.outlineColorLibraryId,
                    canvasObject.zIndex,
                    canvasObject.id,
                    canvasObject.uuid,
                    canvasObject.fontsMap,
                    canvasObject.fontColorsMap,
                    canvasObject.colorLibraryId,
                    canvasObject.fontLibraryId,
                    canvasObject.startAngle,
                    canvasObject.endAngle,
                    canvasObject.locked,
                    canvasObject.visible,
                    canvasObject.name,
                    canvasObject.defaultValue,
                    canvasObject.prefix,
                    canvasObject.suffix,
                    canvasObject.referenceId,
                    canvasObject.isPrintArea,
                    canvasObject.isOverlay,
                    canvasObject.groupId,
                    canvasObject.pageEpsId,
                    canvasObject.getFlip().flipX,
                    canvasObject.getFlip().flipY,
                    canvasObject.text
                );
                updateProductionElementId(canvasObject, canvasObject.id);


                if (skipPreviewElements) {
                    resolve();
                    return;
                }

                // Added text on edit mode
                if (canvasObject.addedOnEdit) {
                    const objectCenterX = canvasObject.left;
                    const objectCenterY = canvasObject.top;
                    const horizontalDiameter = canvasObject.horizontalDiameter;
                    const verticalDiameter = canvasObject.verticalDiameter;

                    addPreviewCircularText(
                        objectCenterX,
                        objectCenterY,
                        canvasObject.color,
                        canvasObject.angle,
                        canvasObject.skewX,
                        canvasObject.skewY,
                        horizontalDiameter,
                        verticalDiameter,
                        canvasObject.fontSize,
                        canvasObject.minSizePx,
                        canvasObject.maxSizePx,
                        canvasObject.caps,
                        canvasObject.fontPath,
                        canvasObject.textAlign,
                        canvasObject.convex,
                        canvasObject.tracking,
                        canvasObject.stroke,
                        canvasObject.strokeWidth,
                        canvasObject.outlineColorsMap,
                        canvasObject.outlineColorLibraryId,
                        canvasObject.zIndex,
                        canvasObject.id,
                        canvasObject.uuid,
                        // add fonts map
                        canvasObject.fontsMap,
                        // add font colors map
                        canvasObject.fontColorsMap,
                        canvasObject.colorLibraryId,
                        canvasObject.fontLibraryId,
                        canvasObject.startAngle,
                        canvasObject.endAngle,
                        true,
                        canvasObject.locked,
                        canvasObject.visible,
                        canvasObject.name,
                        canvasObject.prefix,
                        canvasObject.suffix,
                        canvasObject.referenceId,
                        canvasObject.isPrintArea,
                        canvasObject.isOverlay,
                        canvasObject.groupId,
                        canvasObject.pageEpsId,
                        false,
                        false,
                        canvasObject.text
                    );
                    resolve();
                } else {
                    // If the object was edited, update the parameters in the preview so it shows updated in step2
                    if (canvasObject.isEdited() || canvasObject.editedSize || canvasObject.fontEdited) {
                        let previewElement = null;
                        if (!canvasObject.uuid) {
                            // Find the index of the element
                            let index = getProduct().preview.circularTextsPreview.findIndex(function (obj) {
                                return obj.id === canvasObject.id;
                            });
                            previewElement = getProduct().preview.circularTextsPreview[index];
                        } else {
                            // Find the element by UUID
                            previewElement = getProduct().preview.circularTextsPreview.find(
                                (t) => t.uuid === canvasObject.uuid
                            );
                        }

                        if (previewElement) {
                            // If the size changed, we have to recompute the size on the preview to keep the proportions the same
                            if (canvasObject.editedSize) {
                                // Compute the element width and height
                                const eps = getEps(canvasObject.pageEpsId);
                                const scale = Math.min(
                                    getProduct().preview.width / eps.width,
                                    getProduct().preview.height / eps.height
                                );
                                const horizontalDiameter = canvasObject.horizontalDiameter * scale;
                                const verticalDiameter = canvasObject.verticalDiameter * scale;

                                // Update the element size in the preview
                                previewElement.horizontalDiameter = horizontalDiameter;
                                previewElement.verticalDiameter = verticalDiameter;
                                // Calculate de preview fontSize
                                previewElement.fontSize = GeneralMathUtils.calculateCircularFontSizeFromDimensions(
                                    horizontalDiameter,
                                    canvasObject.horizontalDiameter,
                                    canvasObject.fontSize
                                );
                                const { min, max } = GeneralMathUtils.calculateFontSizesFromDimensions(
                                    horizontalDiameter,
                                    canvasObject.horizontalDiameter,
                                    canvasObject.minSizePx,
                                    canvasObject.maxSizePx
                                );
                                previewElement.minSizePx = min;
                                previewElement.maxSizePx = max;
                            }

                            // Copy edited props to the preview element
                            for (const prop of canvasObject.editedProps) {
                                previewElement[prop] = canvasObject[prop];
                                if (prop === 'ungroup') {
                                    previewElement.groupId = null;
                                } else if (prop === 'strokeWidth') {
                                    const strokeWidth = GeneralMathUtils.calculateStrokeSizeFromDimensions(
                                        previewElement.horizontalDiameter,
                                        canvasObject.horizontalDiameter,
                                        canvasObject.getOutlineWidth()
                                    );
                                    previewElement.strokeWidth = strokeWidth;
                                }
                            }
                        } else if (getProduct().preview.circularTextsPreview.length > 0) {
                            reject(
                                new Error(
                                    'Could not edit the text element with ID ' + (canvasObject.uuid || canvasObject.id)
                                )
                            );
                            return;
                        }
                    }
                    resolve();
                }
            });
        }

        /**
         * Add a placeholder image for the EPS
         * @param {Number}  centerX     Center X position of the text in the preview image in pixels
         * @param {Number}  centerY     Center Y position of the text in the preview image in pixels
         * @param {Number}  rotation    Text rotation in degrees
         * @param {Number}  skewX       Horizontal skew in degrees
         * @param {Number}  skewY       Vertical skew in degrees
         * @param {Number}  width       Text bounds width in pixels
         * @param {Number}  height      Text bounds height in pixels
         * @param {String}  color       Color string in the form of #rrggbb
         * @param {Boolean} fullColor   Defines if the image is full color (raster)
         * @param {Boolean} grading     Defines if color grading should be applied to the image
         * @param {Number}  zIndex      z-index of this element, 0 being the deepest level
         * @param {Number}  id          Unique id of this image placeholder
         * @param {Boolean} hasStaticImage      Whether or not the image is static (wont change)
         * @param {String}  imageUrl    image on Base64
         * @param {String}  maskPath    Path to the mask image if any, null otherwise
         * @param {String}  dynamicImagesPath    Path to the dynamic images if any, null otherwise
         * @param {Boolean}  removeWhiteBackground   Remove white background from images corners
         * @param {Boolean}  RemoveBackground        Remove  background from images with remove.bg
         * @param {Boolean}  isGuide        true if the image is a template product guide image
         * @param {Boolean}  allowEdit    The image can be edited in the preview
         */
        function addEPSPlaceholderImage(data) {
            const {
                centerX,
                centerY,
                rotation,
                skewX,
                skewY,
                width,
                height,
                color,
                fullColor,
                grading,
                RemoveBackground,
                opacity,
                zIndex,
                id,
                uuid,
                hasStaticImage,
                imageUrl,
                maskPath,
                dynamicImagesPath,
                imageLibraryId,
                colors,
                colorLibraryId,
                rotationStep,
                coverMaskArea,
                removeWhiteBackground,
                locked,
                visible,
                name,
                isPrintArea,
                isOverlay,
                isColorOptions,
                podProductImagesMetadata,
                groupId,
                mapConfig,
                starMapConfig,
                cartoonize,
                CutoutFace,
                removeBackgroundCOP,
                CustomilyRemoveBackground = false,
                CustomilyFaceCutOut = false,
                CustomilyImageFilter = 0,
                pageEpsId = null,
                flipX = false,
                flipY = false,
                isLimitImageToPlaceholder,
                textToImage,
                imageToImage = false,
                imageToImageConfig = null,
                imageEffects = null,
                isGuide,
                allowEdit,
                crossword,
                CrosswordConfig = null,
                calendar = false,
                calendarConfig = null,
                outlineWidth,
                outlineColor,
                testingUrl = '',
                isProductGuidelines,
                textMaskUuid,
                isMoonPhase = false,
                initCenterX,
                initCenterY,
                initWidth,
                initHeight,
                initRotation
            } = data;

            let eps = getEps(pageEpsId);
            let image = {
                id: id,
                uuid: uuid,
                centerX: centerX,
                centerY: centerY,
                rotation: rotation,
                width: width,
                height: height,
                color: typeof color === 'object' ? JSON.stringify(color) : color,
                fullColor: fullColor,
                grading: grading,
                RemoveBackground: RemoveBackground,
                opacity: opacity,
                skewX: skewX,
                skewY: skewY,
                zIndex: zIndex,
                hasStaticImage: hasStaticImage,
                imageUrl: imageUrl,
                maskPath: maskPath,
                dynamicImagesPath: dynamicImagesPath,
                imageLibraryId: imageLibraryId,
                colors: colors,
                colorLibraryId: colorLibraryId,
                rotationStep: rotationStep,
                coverMaskArea: coverMaskArea,
                removeWhiteBackground: removeWhiteBackground,
                locked: locked,
                visible: visible,
                name: name,
                isPrintArea: isPrintArea,
                isOverlay: isOverlay,
                isColorOptions: isColorOptions,
                podProductImagesMetadata: podProductImagesMetadata,
                groupId: groupId,
                mapConfig,
                starMapConfig,
                cartoonize,
                CutoutFace,
                removeBackgroundCOP,
                CustomilyRemoveBackground,
                CustomilyFaceCutOut,
                CustomilyImageFilter,
                pageEpsId,
                flipX,
                flipY,
                isLimitImageToPlaceholder,
                textToImage,
                imageToImage,
                imageToImageConfig,
                imageEffects,
                isGuide,
                allowEdit,
                crossword,
                CrosswordConfig,
                calendar,
                calendarConfig,
                outlineWidth,
                outlineColor,
                testingUrl,
                isProductGuidelines,
                textMaskUuid,
                isMoonPhase,
                initCenterX,
                initCenterY,
                initWidth,
                initHeight,
                initRotation
            };
            eps.imagePlaceHoldersEps.push(image);
        }

        /**
         * Add a placeholder image to the product from the given object
         * @param {ImagePlaceholder} canvasObject Object to add to the product
         * @returns {ImagePlaceholder | undefined} Image Placeholder added into preview if the given element was added in edit mode
         */
        function addEPSPlaceholderImageFromObject(canvasObject, skipPreviewElements = false) {
            return $q(async function (resolve, reject) {
                // Does the image has a mask? Upload it but don't upload empty masks (black images of 10x10 pixels generated by ImagePlaceholder) and don't
                //  upload a new mask if it hasn't been changed
                if (
                    canvasObject.maskChanged &&
                    canvasObject.hasMask &&
                    (await ImageUtilService.imageToBase64(canvasObject.originalMaskElement)) !==
                        'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAoAAAAKCAYAAACNMs+9AAAAGElEQVQoU2NkYGD4z0AEYBxViC+UqB88AKk6CgERnGWPAAAAAElFTkSuQmCC'
                ) {
                    // Upload the image, set the path and add the element to the product
                    uploadImageToS3('mask', canvasObject.originalMaskElement).then(function (response) {
                        canvasObject.maskPath = response.Path;
                        addPlaceholderImage(canvasObject);
                    });
                } else if (!canvasObject.hasMask) {
                    canvasObject.maskPath = null;
                    addPlaceholderImage(canvasObject);
                } else {
                    addPlaceholderImage(canvasObject);
                }

                function addPlaceholderImage(canvasObject) {
                    addEPSPlaceholderImage({
                        centerX: canvasObject.left,
                        centerY: canvasObject.top,
                        rotation: canvasObject.angle,
                        skewX: canvasObject.skewX,
                        skewY: canvasObject.skewY,
                        width: canvasObject.getWidth(),
                        height: canvasObject.getHeight(),
                        color: canvasObject.color,
                        fullColor: canvasObject.fullColor,
                        grading: canvasObject.grading,
                        RemoveBackground: canvasObject.RemoveBackground,
                        opacity: 1,
                        zIndex: canvasObject.zIndex,
                        id: canvasObject.id,
                        uuid: canvasObject.uuid,
                        hasStaticImage: canvasObject.hasStaticImage,
                        imageUrl: canvasObject.imageUrl,
                        maskPath: canvasObject.maskPath,
                        dynamicImagesPath: canvasObject.dynamicImagesPath,
                        imageLibraryId: canvasObject.imageLibraryId,
                        colors: canvasObject.colors,
                        colorLibraryId: canvasObject.colorLibraryId,
                        rotationStep: canvasObject.rotationStep,
                        coverMaskArea: canvasObject.coverMaskArea,
                        removeWhiteBackground: canvasObject.removeWhiteBackground,
                        locked: canvasObject.locked,
                        visible: canvasObject.visible,
                        name: canvasObject.name,
                        isPrintArea: canvasObject.isPrintArea,
                        isOverlay: canvasObject.isOverlay,
                        isColorOptions: canvasObject.isColorOptions,
                        podProductImagesMetadata: canvasObject.podProductImagesMetadata,
                        groupId: canvasObject.groupId,
                        mapConfig: canvasObject.mapConfig,
                        starMapConfig: canvasObject.starMapConfig,
                        cartoonize: canvasObject.cartoonize,
                        CutoutFace: canvasObject.CutoutFace,
                        removeBackgroundCOP: canvasObject.removeBackgroundCOP,
                        CustomilyRemoveBackground: canvasObject.CustomilyRemoveBackground,
                        CustomilyFaceCutOut: canvasObject.CustomilyFaceCutOut,
                        CustomilyImageFilter: canvasObject.CustomilyImageFilter,
                        pageEpsId: canvasObject.pageEpsId,
                        flipX: canvasObject.getFlip().flipX,
                        flipY: canvasObject.getFlip().flipY,
                        isLimitImageToPlaceholder: canvasObject.isLimitImageToPlaceholder,
                        textToImage: canvasObject.textToImage,
                        imageToImage: canvasObject.imageToImage,
                        imageToImageConfig: canvasObject.imageToImageConfig,
                        imageEffects: canvasObject.imageEffects,
                        isGuide: canvasObject.isGuide,
                        allowEdit: canvasObject.allowEdit,
                        crossword: canvasObject.crossword,
                        CrosswordConfig: canvasObject.CrosswordConfig,
                        calendar: canvasObject.calendar,
                        calendarConfig: canvasObject.calendarConfig,
                        outlineWidth: canvasObject.outlineWidth,
                        outlineColor: canvasObject.outlineColor,
                        testingUrl: canvasObject.testingUrl,
                        isProductGuidelines: canvasObject.isProductGuidelines,
                        textMaskUuid: canvasObject.textMaskUuid,
                        isMoonPhase: canvasObject.isMoonPhase,
                        initCenterX: canvasObject.initCenterX,
                        initCenterY: canvasObject.initCenterY,
                        initWidth: canvasObject.initWidth,
                        initHeight: canvasObject.initHeight,
                        initRotation: canvasObject.initRotation
                    });
                    updateProductionElementId(canvasObject, canvasObject.id);

                    if (skipPreviewElements) {
                        resolve();
                        return;
                    }

                    // Placeholders added on edit mode
                    if (canvasObject.addedOnEdit) {
                        try {
                            const image = addPreviewPlaceholderImage({
                                centerX: canvasObject.left,
                                centerY: canvasObject.top,
                                rotation: canvasObject.angle,
                                skewX: canvasObject.skewX,
                                skewY: canvasObject.skewY,
                                width: canvasObject.width,
                                height: canvasObject.height,
                                color: canvasObject.color,
                                fullColor: canvasObject.fullColor,
                                grading: canvasObject.grading,
                                opacity: 1,
                                zIndex: canvasObject.zIndex,
                                id: canvasObject.id,
                                uuid: canvasObject.uuid,
                                hasStaticImage: canvasObject.hasStaticImage,
                                imageUrl: canvasObject.imageUrl,
                                maskPath: canvasObject.maskPath,
                                dynamicImagesPath: canvasObject.dynamicImagesPath,
                                imageLibraryId: canvasObject.imageLibraryId,
                                colors: canvasObject.colors,
                                colorLibraryId: canvasObject.colorLibraryId,
                                rotationStep: canvasObject.rotationStep,
                                coverMaskArea: canvasObject.coverMaskArea,
                                removeWhiteBackground: canvasObject.removeWhiteBackground,
                                RemoveBackground: canvasObject.RemoveBackground,
                                addedOnEdit: true,
                                locked: canvasObject.locked,
                                visible: canvasObject.visible,
                                name: canvasObject.name,
                                isPrintArea: canvasObject.isPrintArea,
                                isOverlay: canvasObject.isOverlay,
                                isColorOptions: canvasObject.isColorOptions,
                                podProductImagesMetadata: canvasObject.podProductImagesMetadata,
                                groupId: canvasObject.groupId,
                                mapConfig: canvasObject.mapConfig,
                                starMapConfig: canvasObject.starMapConfig,
                                cartoonize: canvasObject.cartoonize,
                                CutoutFace: canvasObject.CutoutFace,
                                removeBackgroundCOP: canvasObject.removeBackgroundCOP,
                                CustomilyRemoveBackground: canvasObject.CustomilyRemoveBackground,
                                CustomilyFaceCutOut: canvasObject.CustomilyFaceCutOut,
                                CustomilyImageFilter: canvasObject.CustomilyImageFilter,
                                pageEpsId: canvasObject.pageEpsId,
                                flipX: canvasObject.getFlip()?.flipX || false,
                                flipY: canvasObject.getFlip()?.flipY || false,
                                isLimitImageToPlaceholder: canvasObject.isLimitImageToPlaceholder,
                                textToImage: canvasObject.textToImage,
                                imageToImage: canvasObject.imageToImage,
                                imageToImageConfig: canvasObject.imageToImageConfig,
                                imageEffects: canvasObject.imageEffects,
                                isGuide: canvasObject.isGuide,
                                allowEdit: canvasObject.allowEdit,
                                crossword: canvasObject.crossword,
                                CrosswordConfig: canvasObject.CrosswordConfig,
                                calendar: canvasObject.calendar,
                                calendarConfig: canvasObject.calendarConfig,
                                enablePerspective: false, // no canvasObject.enablePerspective for step1
                                perspectiveCoords: null, // no canvasObject.perspectiveCoords for step1
                                outlineWidth: canvasObject.outlineWidth,
                                outlineColor: canvasObject.outlineColor,
                                testingUrl: canvasObject.testingUrl,
                                isProductGuidelines: canvasObject.isProductGuidelines,
                                textMaskUuid: canvasObject.textMaskUuid,
                                isMoonPhase: canvasObject.isMoonPhase,
                                initCenterX: canvasObject.initCenterX,
                                initCenterY: canvasObject.initCenterY,
                                initWidth: canvasObject.initWidth,
                                initHeight: canvasObject.initHeight,
                                initRotation: canvasObject.initRotation
                            });

                            resolve({
                                previewElement: image,
                                newlyAdded: true
                            });
                        } catch (error) {
                            reject(error);
                        }
                    }
                    // If the object was edited, update the parameters in the preview so it shows updated in step2
                    else {
                        let previewElement = null;
                        if (!canvasObject.uuid) {
                            let index = getProduct().preview.imagePlaceHoldersPreview.findIndex(function (obj) {
                                return obj.id === canvasObject.id;
                            });
                            previewElement = getProduct().preview.imagePlaceHoldersPreview[index];
                        } else {
                            previewElement = getProduct().preview.imagePlaceHoldersPreview.find(
                                (i) => i.uuid === canvasObject.uuid
                            );
                        }

                        if (canvasObject.isEdited() || canvasObject.editedSize) {
                            if (previewElement) {
                                // If the size changed, we have to recompute the size on the preview to keep the proportions the same
                                if (canvasObject.editedSize) {
                                    // Compute the element width and height
                                    let eps = getEps(canvasObject.pageEpsId);
                                    let scale = Math.min(
                                        getProduct().preview.width / eps.width,
                                        getProduct().preview.height / eps.height
                                    );
                                    let boundingRectWidth = canvasObject.width * scale;
                                    let boundingRectHeight = canvasObject.height * scale;

                                    previewElement.width = boundingRectWidth;
                                    previewElement.height = boundingRectHeight;
                                }

                                // Copy edited props to the preview element
                                for (const prop of canvasObject.editedProps) {
                                    if (prop === 'mask') {
                                        previewElement['maskPath'] = canvasObject['maskPath'];
                                    } else if (prop === 'ungroup') {
                                        previewElement.groupId = null;
                                    } else if (prop === 'initImagePosition') {
                                        const scale = Math.min(
                                            previewElement.width / canvasObject.width,
                                            previewElement.height / canvasObject.height
                                        );
                                        previewElement.initCenterX = canvasObject.initCenterX
                                            ? canvasObject.initCenterX * scale
                                            : null;
                                        previewElement.initCenterY = canvasObject.initCenterY
                                            ? canvasObject.initCenterY * scale
                                            : null;
                                        previewElement.initWidth = canvasObject.initWidth
                                            ? canvasObject.initWidth * scale
                                            : null;
                                        previewElement.initHeight = canvasObject.initHeight
                                            ? canvasObject.initHeight * scale
                                            : null;
                                        previewElement.initRotation = canvasObject.initRotation;
                                    } else {
                                        previewElement[prop] = canvasObject[prop];
                                    }
                                }
                            } else if (getProduct().preview.imagePlaceHoldersPreview.length > 0) {
                                reject(
                                    new Error(
                                        'Could not edit the image element with ID ' +
                                            (canvasObject.uuid || canvasObject.id)
                                    )
                                );
                                return;
                            }
                        }

                        resolve({
                            previewElement: previewElement,
                            newlyAdded: false
                        });
                    }
                }
            });
        }

        /**
         *
         * @param {Number}  centerX             Center X position of the vector EPS [pixels]
         * @param {Number}  centerY             Center Y position of the vector EPS [pixels]
         * @param {Number}  rotation            Rotation [degrees]
         * @param {Number}  skewX               Horizontal skew [degrees]
         * @param {Number}  skewY               Vertical skew [degrees]
         * @param {Number}  width               Width [pixels]
         * @param {Number}  height              Height [pixels]
         * @param {Number}  opacity             Opacity from 0 to 1
         * @param {Array}   vectors             Array of vectors for each position
         * @param colors
         * @param allowUserUpload               Wether to allow the user to upload an SVG element on the product preview
         * @param zIndex                        z-index of this element, 0 being the deepest level
         * @param id
         * @param uuid
         * @param colorLibraryId
         * @param vectorLibraryId
         * @param applyColoring
         * @param coverMaskArea
         * @param rotationStep
         * @param locked
         * @param visible
         * @param name
         * @param isPrintArea
         * @param isOverlay
         * @param groupId
         * @param starMapConfig
         * @param qrConfig
         * @param contentIsPdf
         * @param vectorContent
         * @param allowEdit
         * @param pageEpsId
         * @param flipX
         * @param flipY
         * @param isSolidBackground
         */
        function addEPSVectorEPS(
            centerX,
            centerY,
            rotation,
            skewX,
            skewY,
            width,
            height,
            opacity,
            vectors,
            colors,
            allowUserUpload,
            zIndex,
            id,
            uuid,
            colorLibraryId,
            vectorLibraryId,
            applyColoring,
            coverMaskArea,
            rotationStep,
            locked,
            visible,
            name,
            isPrintArea,
            isOverlay,
            groupId,
            starMapConfig,
            qrConfig,
            contentIsPdf,
            vectorContent,
            allowEdit,
            pageEpsId = null,
            flipX = false,
            flipY = false,
            isSolidBackground
        ) {
            let eps = getEps(pageEpsId);
            let vectorEPSEps = {
                id: id,
                uuid: uuid,
                centerX: centerX,
                centerY: centerY,
                rotation: rotation,
                width: width,
                height: height,
                opacity: opacity,
                skewX: skewX,
                skewY: skewY,
                vectors: vectors,
                zIndex: zIndex,
                allowUserUpload: allowUserUpload,
                colors: colors,
                colorLibraryId: colorLibraryId,
                vectorLibraryId: vectorLibraryId,
                applyColoring: applyColoring,
                coverMaskArea: coverMaskArea,
                rotationStep: rotationStep,
                locked: locked,
                visible: visible,
                name: name,
                isPrintArea: isPrintArea,
                isOverlay: isOverlay,
                groupId: groupId,
                starMapConfig: starMapConfig,
                qrConfig: qrConfig,
                contentIsPdf: contentIsPdf,
                allowEdit: allowEdit,
                pageEpsId,
                flipX,
                flipY,
                isSolidBackground
            };
            if (contentIsPdf) {
                vectorEPSEps.pdf = vectorContent;
            } else {
                vectorEPSEps.svgString = vectorContent;
            }
            eps.vectorEPSEps.push(vectorEPSEps);
        }

        /**
         * Add a vector EPS to the product from the given object
         * @param {VectorPlaceholder} canvasObject Object to add to the product
         */
        function addEPSVectorEPSFromObject(canvasObject, skipPreviewElements = false) {
            return $q(function (resolve, reject) {
                addEPSVectorEPS(
                    canvasObject.left,
                    canvasObject.top,
                    canvasObject.angle,
                    canvasObject.skewX,
                    canvasObject.skewY,
                    canvasObject.width * canvasObject.scaleX,
                    canvasObject.height * canvasObject.scaleY,
                    1,
                    canvasObject.vectors,
                    canvasObject.colors,
                    canvasObject.allowUserUpload,
                    canvasObject.zIndex,
                    canvasObject.id,
                    canvasObject.uuid,
                    canvasObject.colorLibraryId,
                    canvasObject.vectorLibraryId,
                    canvasObject.applyColoring,
                    canvasObject.coverMaskArea,
                    canvasObject.rotationStep,
                    canvasObject.locked,
                    canvasObject.visible,
                    canvasObject.name,
                    canvasObject.isPrintArea,
                    canvasObject.isOverlay,
                    canvasObject.groupId,
                    canvasObject.starMapConfig,
                    canvasObject.qrConfig,
                    canvasObject.contentIsPdf,
                    canvasObject.contentIsPdf ? canvasObject.pdf : canvasObject.svgString,
                    canvasObject.allowEdit,
                    canvasObject.pageEpsId,
                    canvasObject.getFlip().flipX,
                    canvasObject.getFlip().flipY,
                    canvasObject.isSolidBackground
                );
                updateProductionElementId(canvasObject, canvasObject.id);

                if (skipPreviewElements) {
                    resolve();
                    return;
                }

                // Added vector svg on edit mode?
                if (canvasObject.addedOnEdit) {
                    const objectCenterX = canvasObject.left;
                    const objectCenterY = canvasObject.top;
                    const newWidth = canvasObject.width;
                    const newHeight = canvasObject.height;

                    addPreviewVectorEPS(
                        objectCenterX,
                        objectCenterY,
                        canvasObject.angle,
                        canvasObject.skewX,
                        canvasObject.skewY,
                        newWidth,
                        newHeight,
                        1,
                        canvasObject.vectors,
                        canvasObject.colors,
                        canvasObject.allowUserUpload,
                        canvasObject.zIndex,
                        canvasObject.id,
                        canvasObject.uuid,
                        canvasObject.colorLibraryId,
                        canvasObject.vectorLibraryId,
                        canvasObject.applyColoring,
                        canvasObject.coverMaskArea,
                        canvasObject.rotationStep,
                        true,
                        canvasObject.locked,
                        canvasObject.visible,
                        canvasObject.name,
                        canvasObject.isPrintArea,
                        canvasObject.isOverlay,
                        canvasObject.groupId,
                        canvasObject.starMapConfig,
                        canvasObject.qrConfig,
                        canvasObject.contentIsPdf,
                        canvasObject.contentIsPdf ? canvasObject.pdf : canvasObject.svgString,
                        canvasObject.allowEdit,
                        canvasObject.pageEpsId,
                        canvasObject.isSolidBackground
                    );
                    resolve();
                } else {
                    // If the object was edited, update the parameters in the preview so it shows updated in step2
                    if (canvasObject.isEdited() || canvasObject.editedSize) {
                        let previewElement = null;
                        if (!canvasObject.uuid) {
                            // Find the index of the element
                            let index = getProduct().preview.vectorEPSPreview.findIndex(function (obj) {
                                return obj.id === canvasObject.id;
                            });
                            previewElement = getProduct().preview.vectorEPSPreview[index];
                        } else {
                            previewElement = getProduct().preview.vectorEPSPreview.find(
                                (v) => v.uuid === canvasObject.uuid
                            );
                        }

                        if (previewElement) {
                            // If the size changed, we have to recompute the size on the preview to keep the proportions the same
                            if (canvasObject.editedSize) {
                                // Compute the element widgth and height
                                let eps = getEps(canvasObject.pageEpsId);
                                let scale = Math.min(
                                    getProduct().preview.width / eps.width,
                                    getProduct().preview.height / eps.height
                                );
                                let boundingRectWidth = canvasObject.width * scale;
                                let boundingRectHeight = canvasObject.height * scale;

                                previewElement.width = boundingRectWidth;
                                previewElement.height = boundingRectHeight;
                            }

                            // Copy edited props to the preview element
                            for (const prop of canvasObject.editedProps) {
                                previewElement[prop] = canvasObject[prop];
                            }

                            // Copy vector content
                            if (canvasObject.contentIsPdf) {
                                previewElement.pdf = canvasObject.pdf;
                            } else {
                                if (canvasObject.svgString) {
                                    previewElement.svgString = canvasObject.svgString;
                                }
                            }
                        }
                    }
                    resolve();
                }
            });
        }

        function setEPSBaseImagePath(path, epsId = null) {
            let eps = getEps(epsId);
            eps.baseImage = path;
        }

        function setEPSBaseEPSPath(path, epsId = null) {
            let eps = getEps(epsId);
            eps.baseEps = path;
        }

        function getProduct() {
            return product;
        }

        function getProductsCount(searchString) {
            ProductAPIService.getProductsCount(searchString);
        }

        function saveProduct(product) {
            return ProductAPIService.saveProduct(product);
        }

        /**
         * Update the id of the given element and also change the id of the associated element
         *  in the preview if any.
         * @param {Object} element Canvas element to update.
         * @param {Number} newId New id for the element.
         */
        function updateProductionElementId(element, newId) {
            element.id = newId;
            let uuid = element.uuid;

            if (uuid) {
                let preview = getProduct().preview;
                let elements = preview.textsPreview
                    .concat(preview.circularTextsPreview)
                    .concat(preview.imagePlaceHoldersPreview)
                    .concat(preview.vectorEPSPreview);
                let previewElement = elements.find((e) => e.uuid === uuid);

                if (previewElement) {
                    previewElement.id = newId;
                }
            }
        }

        function getLastPreviewId() {
            let maxTextId = 0;
            let maxCircularTextId = 0;
            let maxImageId = 0;
            let maxVectorId = 0;
            let sortFunction = function (a, b) {
                return b.id - a.id;
            };

            //sorts all list in reverse order and return the largest id for each list
            if (getProduct().preview.textsPreview.length > 0) {
                maxTextId = getProduct().preview.textsPreview.sort(sortFunction)[0].id;
            }
            if (getProduct().preview.circularTextsPreview.length > 0) {
                maxCircularTextId = getProduct().preview.circularTextsPreview.sort(sortFunction)[0].id;
            }
            if (getProduct().preview.imagePlaceHoldersPreview.length > 0) {
                maxImageId = getProduct().preview.imagePlaceHoldersPreview.sort(sortFunction)[0].id;
            }
            if (getProduct().preview.vectorEPSPreview.length > 0) {
                maxVectorId = getProduct().preview.vectorEPSPreview.sort(sortFunction)[0].id;
            }

            return Math.max(maxTextId, maxCircularTextId, maxImageId, maxVectorId);
        }

        function getAllIndexes(arr, val) {
            let indexes = [],
                i;
            for (i = 0; i < arr.length; i++) if (arr[i].id === val) indexes.push(i);

            return indexes;
        }

        /**
         * Get the EPS element corresponding to the preview element with the given id
         * @param {Number} id id of the element in the preview
         */
        function getEpsElementFromPreviewElement(targetElement) {
            let eps = getEps(targetElement.pageEpsId);

            let element = eps.textsEps.find((e) =>
                targetElement.uuid ? e.uuid === targetElement.uuid : e.id === targetElement.id
            );
            if (element) return element;

            element = eps.circularTextsEps.find((e) =>
                targetElement.uuid ? e.uuid === targetElement.uuid : e.id === targetElement.id
            );
            if (element) return element;

            element = eps.imagePlaceHoldersEps.find((e) =>
                targetElement.uuid ? e.uuid === targetElement.uuid : e.id === targetElement.id
            );
            if (element) return element;

            element = eps.vectorEPSEps.find((e) =>
                targetElement.uuid ? e.uuid === targetElement.uuid : e.id === targetElement.id
            );
            if (element) return element;

            return element;
        }

        function isPreviewOnlyElement(element, epsId = null) {
            if (!element) return false;
            let eps = getEps(element.pageEpsId || epsId);

            if (!eps) {
                return false;
            }

            if (element && element.dynamicImagesPath) {
                const placeholder = [...eps.imagePlaceHoldersEps, ...eps.vectorEPSEps];

                const isStep1DynamicImage = element.uuid
                    ? placeholder.some((i) => i.uuid === element.uuid)
                    : placeholder.some((i) => i.id == element.id);

                return !isStep1DynamicImage;
            }

            return false;
        }

        return {
            loadProduct: loadProduct,

            addGroups: addGroups,
            getGroups: getGroups,
            updatePreviewGroups: updatePreviewGroups,

            clearEPS: clearEPS,
            clearPreview: clearPreview,
            clearPreviewElements: clearPreviewElements,
            clearProduct: clearProduct,
            setProductId: setProductId,
            getProductId: getProductId,
            setProductName: setProductName,
            setMelcoReferenceId: setMelcoReferenceId,
            setProductFormat: setProductFormat,
            setProductColorMode: setProductColorMode,
            setMergeFiles: setMergeFiles,
            setPreviewImage: setPreviewImage,
            setPreviewBgColor: setPreviewBgColor,
            setPreviewImgSize: setPreviewImgSize,
            addPreviewText: addPreviewText,
            addPreviewPlaceholderImage: addPreviewPlaceholderImage,
            setEPSSize: setEPSSize,
            setEPSdpi: setEPSdpi,
            setExportPPI: setExportPPI,
            setEPSMirroring: setEPSMirroring,
            setEpsMergeOutput: setEpsMergeOutput,
            setNeedColorsUpgrade: setNeedColorsUpgrade,
            getNeedColorsUpgrade: getNeedColorsUpgrade,

            addPreviewCircularText: addPreviewCircularText,

            addEPSText: addEPSText,
            addEPSTextFromObject: addEPSTextFromObject,
            addEPSCircularTextFromObject: addEPSCircularTextFromObject,

            addEPSPlaceholderImage: addEPSPlaceholderImage,
            addEPSPlaceholderImageFromObject: addEPSPlaceholderImageFromObject,

            addEPSVectorEPS: addEPSVectorEPS,
            addEPSVectorEPSFromObject: addEPSVectorEPSFromObject,
            addPreviewVectorEPS: addPreviewVectorEPS,

            setEPSBaseImagePath: setEPSBaseImagePath,
            setEPSBaseEPSPath: setEPSBaseEPSPath,
            getProduct: getProduct,
            getProductsCount: getProductsCount,
            getPreviewImage: getPreviewImage,
            getPreviewBgColor: getPreviewBgColor,
            saveProduct: saveProduct,
            removeFromPreview: removeFromPreview,
            recoverFromPreview: recoverFromPreview,
            clearObjectsDeleteOnEPS: clearObjectsDeleteOnEPS,
            getLastPreviewId: getLastPreviewId,
            getEpsElementFromPreviewElement: getEpsElementFromPreviewElement,
            updateProductionElementId: updateProductionElementId,
            isPreviewOnlyElement: isPreviewOnlyElement,

            setProductTemplatePreviewTargetSize: setProductTemplatePreviewTargetSize,
            getProductTemplatePreviewTargetSize: getProductTemplatePreviewTargetSize,
            setPodAssignments: setPodAssignments,
            getPodAssignments: getPodAssignments,
            setPod: setPod,
            getPod: getPod,
            setDescription: setDescription,
            getDescription: getDescription,
            getProductColorsFilenames: getProductColorsFilenames,
            setProductColorsFilenames: setProductColorsFilenames,
            setTemplateProductVariants: setTemplateProductVariants,
            getTemplateProductVariants: getTemplateProductVariants,
            clearChildTemplateProduct: clearChildTemplateProduct,
            setChildTemplateProduct: setChildTemplateProduct,
            getChildTemplateProduct: getChildTemplateProduct,
            setPreviewColorOptionsMetadata: setPreviewColorOptionsMetadata,
            getPreviewColorOptionsMetadata: getPreviewColorOptionsMetadata,
            setIsProductColorModified: setIsProductColorModified,
            getIsProductColorModified: getIsProductColorModified,
            getStarMapMutex: getStarMapMutex,
            getQrMutex: getQrMutex,
            setEditMode: setEditMode,
            getEditMode: getEditMode,
            getEps: getEps,
            getElementsIdAndTestingValues: getElementsIdAndTestingValues,
            setTestingValues: setTestingValues,
            setImagesTestingValues: setImagesTestingValues,
            addNewEPS: addNewEPS,
            addListEPS: addListEPS,
            getListEPS: getListEPS,
            updateEPSFromPreview: updateEPSFromPreview,
            deleteEPS: deleteEPS,
            setProductSnapshotUrl: setProductSnapshotUrl,
            getProductSnapshotUrl: getProductSnapshotUrl,
            setProductThumbnail: setProductThumbnail,
            getProductThumbnail: getProductThumbnail
        };
    }
})();
